import { useState } from "react";
import { useAuth } from "react-oidc-context";
import { Button, HoverTooltip, LogOut, Spinner } from "@repo/ui";
import { useSignOut } from "~/hooks/use-sign-out";

const readClaim = (profile: Record<string, unknown> | undefined, key: string) => {
  const value = profile?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

const getInitials = (name: string) => {
  const parts = name.split(/[\s._@-]+/).filter(Boolean);
  if (!parts?.length) return "??";
  const first = parts[0]?.[0] ?? "??";
  const last = parts[0]?.[2] ?? "??";
  return (first + last).toUpperCase() || "?";
};

export const SidebarUserFooter = () => {
  const auth = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const profile = auth.user?.profile;
  const email = readClaim(profile, "email");
  const role = readClaim(profile, "role");
  const displayName =
    readClaim(profile, "display_name") ??
    readClaim(profile, "name") ??
    readClaim(profile, "preferred_username") ??
    email ??
    "User";
  const secondaryText = email && email !== displayName ? email : role;
  const { signOut } = useSignOut();
  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <div className="flex h-full items-center gap-3 px-4">
      <div
        aria-hidden
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-blue-500 to-blue-700 text-xs font-semibold tracking-wide text-white shadow-sm ring-2 ring-white select-none"
      >
        {getInitials(displayName)}
      </div>

      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm font-semibold text-gray-900" title={displayName}>
          {displayName}
        </p>
        {secondaryText && (
          <p className="truncate text-xs text-gray-500" title={secondaryText}>
            {secondaryText}
          </p>
        )}
      </div>

      <HoverTooltip content="Sign out">
        <Button
          type="button"
          variant="ghost"
          size="icon_md"
          aria-label="Sign out"
          aria-busy={isSigningOut}
          disabled={isSigningOut}
          onClick={handleSignOut}
          icon={isSigningOut ? <Spinner className="size-4" /> : <LogOut className="size-4.5" />}
          className="shrink-0 text-gray-500 hover:bg-red-50 hover:text-red-600 focus-visible:ring-red-500 active:bg-red-100"
        />
      </HoverTooltip>
    </div>
  );
};

SidebarUserFooter.displayName = "SidebarUserFooter";
